import mapboxgl from "mapbox-gl"; // This is a dependency of react-map-gl even if you didn't explicitly install it
import "mapbox-gl/dist/mapbox-gl.css";
import React from "react";
import Map, {
  GeolocateControl,
  MapProvider,
  Marker,
  Popup,
} from "react-map-gl";
import Swal from "sweetalert2";
import { config } from "../../../../config/index";
import getLocation from "../../../../helpers/geolocator";
import "./styles.css";
const {
  MAP_BOX_API_URL,
  MAP_BOX_API_TOKEN,
  LOCATION_API_URL,
  LOCATION_API_SECRET_KEY,
} = config;

// prettier-ignore
// eslint-disable-next-line import/no-webpack-loader-syntax
mapboxgl.workerClass = require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default;

const ConfirmLocation = ({
  featuresOnOff,
  setConfirmLocation,
  masterClockInOutHandler,
  apiLocation,
  setApiLocation,
  setFeaturesOnOff,
  coordinates,
  setCoordinates,
  timeClockType,
  activeClient,
  isInsideCircle,
  distanceTo,
  circleRadius,
  sites,
  setFeatures,
  defaultSiteData,
  t,
}) => {
  const [viewState, setViewState] = React.useState({
    longitude: 103.8307668795241,
    latitude: 1.3277931546983268,
    zoom: 10.697175623863698,
    pitch: 40.53850326912702,
    bearing: -15.886689408000393,
  });
  const [viewMarkerState, setMarkerState] = React.useState(null);
  const [showPopup, setShowPopup] = React.useState(true);
  const [isLoadingMap, setIsLoadingMap] = React.useState(true);
  const [isLoadingLocation, setIsLoadingLocation] = React.useState(false);
  const geolocateControlRef = React.useRef(null);

  React.useEffect(() => {
    featuresOnOff.feature === "geolocation" && enableGeoLocation("auto");
  }, [featuresOnOff]);

  React.useEffect(() => {
    if (coordinates?.Lat) {
      setMarkerState({
        longitude: coordinates.Long,
        latitude: coordinates.Lat,
      });
      setViewState({
        longitude: coordinates.Long,
        latitude: coordinates.Lat,
        zoom: 14,
      });
      setIsLoadingLocation(false); // Stop loading when coordinates are set
    }
  }, [coordinates]);

  const enableGeoLocation = async (callType) => {
    setIsLoadingLocation(true); // Start loading
    callType === "manual" && newTriggerForloading();

    try {
      const position = await getLocation();

      if (position.Long != 0) {
        const res = await fetch(
          LOCATION_API_URL +
            `${position.Long},${position.Lat}` +
            LOCATION_API_SECRET_KEY
        );

        const data = await res.json();

        if (data.features.length == 0) {
          setApiLocation(t("Location not found"));
        } else {
          setApiLocation(data.features[0].place_name);
          setCoordinates(position);
        }

        setShowPopup(true);
      } else {
        // Set location to site name if available
        const siteName =
          defaultSiteData?.site_name ||
          activeClient?.site_name ||
          t("Unknown Site");

        Swal.fire({
          title: `${t("Info")}`,
          text: `${t("Record will be saved without location")}`,
          icon: "warning",
        });

        setApiLocation(siteName);
        setIsLoadingLocation(false); // Stop loading
      }
    } catch (error) {
      console.error("Error getting location:", error);
      setIsLoadingLocation(false); // Stop loading on error
    }

    callType === "manual" &&
      setTimeout(() => {
        Swal.close();
      }, 2000);
  };

  const reverseGeocode = async (longitude, latitude) => {
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${longitude},${latitude}.json?access_token=${MAP_BOX_API_TOKEN}`
      );
      const data = await response.json();

      if (data.features && data.features.length > 0) {
        const address = data.features[0].place_name;
        setApiLocation(address);
      } else {
        console.error("No address found for these coordinates.");
      }
    } catch (error) {
      console.error("Error during reverse geocoding:", error);
    } finally {
      setIsLoadingLocation(false); // Stop loading after reverse geocode completes
    }
  };

  const enableGeoFencing = async () => {
    try {
      if (coordinates?.Lat) {
        const point = [coordinates.Lat, coordinates.Long];

        const inside = await isInsideCircle(
          circleRadius.center,
          point,
          circleRadius.radius
        );

        const distance = await distanceTo(circleRadius.center, point);

        // Convert distance to meters
        const meterDistance = distance.toFixed(1) + " meters";

        if (!inside) {
          Swal.fire({
            title: `${t(
              "Sorry, It seems you are not on the site to clock in"
            )}`,
            text: `${t("You are")} ${meterDistance} ${t("away from the site")}`,
            icon: "error",
            showLoaderOnConfirm: true,
          }).then((result) => {
            if (result.isConfirmed) {
              // delete browser cache and hard reload
              //window.location.reload(true);
            }
          });
        } else {
          setConfirmLocation(apiLocation);
          setFeaturesOnOff({
            feature: null,
          });
          setFeatures((prev) => {
            return prev.map((item) =>
              item.feature === "geolocation" ? { ...item, trail: true } : item
            );
          });
          masterClockInOutHandler(timeClockType);
        }
      } else {
        Swal.fire({
          title: `${t("Location not recognized")}`,
          text: `${t(
            "Unable to determine your location. Please enable location services in your browser settings"
          )}`,
          icon: "error",
          showLoaderOnConfirm: true,
        });
      }
    } catch (err) {
      console.error("Failed to fetch location:", err);
    }
  };

  const newTriggerForloading = () =>
    Swal.fire({
      title: `${t("Loading, please wait")}`,
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
      customClass: {
        title: "swal-title-custom",
      },
    });

  return (
    <React.Fragment>
      {featuresOnOff.feature === "geolocation" && (
        <div className="location__container">
          <MapProvider>
            {(isLoadingMap || isLoadingLocation) && (
              <div
                style={{
                  position: "absolute",
                  top: "35%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 1000,
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                  padding: "20px",
                  borderRadius: "8px",
                  fontWeight: "bold",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
                {t("Processing...")}
              </div>
            )}
            <Map
              {...viewState}
              id="siteMap"
              onMove={(evt) => {
                setViewState(evt.viewState);
              }}
              onLoad={() => setIsLoadingMap(false)}
              style={{ width: "100%", height: 300, marginTop: 10 }}
              mapStyle={MAP_BOX_API_URL}
              mapboxAccessToken={MAP_BOX_API_TOKEN}
            >
              {/* GeolocateControl for showing the user's current location */}
              <GeolocateControl
                ref={geolocateControlRef}
                position="top-right"
                trackUserLocation={true}
                showUserLocation={true}
                onGeolocate={(position) => {
                  setIsLoadingLocation(true); // Start loading when geolocate triggers
                  const { latitude, longitude } = position.coords;

                  setMarkerState({
                    longitude: longitude,
                    latitude: latitude,
                  });

                  setViewState({
                    ...viewState,
                    latitude: latitude,
                    longitude: longitude,
                    zoom: 14,
                  });

                  setApiLocation("Fetching address...");

                  // Trigger reverse geocoding to get the address
                  reverseGeocode(longitude, latitude);
                }}
              />

              {viewMarkerState && (
                <>
                  {showPopup && (
                    <Popup
                      longitude={viewMarkerState.longitude}
                      latitude={viewMarkerState.latitude}
                      anchor="top"
                      onClose={() => setShowPopup(false)}
                      closeOnClick={false}
                    >
                      <span>{apiLocation}</span>
                    </Popup>
                  )}
                  <Marker
                    longitude={viewMarkerState.longitude}
                    latitude={viewMarkerState.latitude}
                    color="blue"
                  />
                </>
              )}
            </Map>
          </MapProvider>

          <div
            className="location__container-confirm"
            style={{
              marginTop: 30,
            }}
          >
            <h6
              className="refresh-location mb-3"
              onClick={() => {
                enableGeoLocation("manual");
              }}
            >
              {t("Click here to refresh your location")}
            </h6>
            <button
              type="button"
              className="btn btn-design btn-brand btn-clockinout"
              style={{
                backgroundColor: "#5156BE",
              }}
              onClick={() => {
                geolocateControlRef.current.trigger();

                if (defaultSiteData?.enable_geofencing) {
                  enableGeoFencing();
                } else {
                  setConfirmLocation(apiLocation);
                  setFeaturesOnOff({
                    feature: null,
                  });
                  setFeatures((prev) => {
                    return prev.map((item) =>
                      item.feature === "geolocation"
                        ? { ...item, trail: true }
                        : item
                    );
                  });
                  masterClockInOutHandler(timeClockType);
                }
              }}
            >
              {t("Confirm Location")}
            </button>
            <p>{t("Try moving around if this is not your current position")}</p>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default ConfirmLocation;
